// Path: /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/components/header/HeaderMobile.tsx
// Version: v3.0.2
// Description: Mobile Header - Tablet Drawer 35%, Mobile 61%

import { User, ChevronRight, ChevronLeft, Globe, X, Check } from 'lucide-react';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../lang/index.lang.tsx';
import { getNavigationConfig, DropdownType } from './navigationConfig.tsx';

// ==========================================
// KONSTANTEN
// ==========================================
const HEADER_HEIGHT = 50;
const LOGO_WIDTH = 100;
const ICON_SIZE = 17;
const HAMBURGER_SIZE = 32;
const HAMBURGER_LINE_WIDTH = 17;
const HAMBURGER_LINE_HEIGHT = 1.5;
const HAMBURGER_LINE_SPACING = 5;

// Drawer Konstanten
const DRAWER_WIDTH_MOBILE = '61vw'; // Mobile: 61%
const DRAWER_WIDTH_TABLET = '35vw'; // Tablet: 35%
const TABLET_BREAKPOINT = 768; // px - md breakpoint
const HANDLE_WIDTH = 24; // px - Breite des Schließen-Handles
const TRANSITION_OPEN = 500; // ms - langsamer beim Öffnen
const TRANSITION_CLOSE = 300; // ms - schneller beim Schließen
const MENU_ITEM_HEIGHT = 56; // px - einheitliche Höhe für alle Menüpunkte
const HOVER_COLOR = '#37acdc'; // ESTATA Blau

// ==========================================
// INTERFACES
// ==========================================
interface HeaderMobileProps {
  navigationConfig: ReturnType<typeof getNavigationConfig>;
}

interface LinkItem {
  name: string;
  href: string;
  isHighlight?: boolean;
}

// ==========================================
// COMPONENT
// ==========================================
const HeaderMobile: React.FC<HeaderMobileProps> = ({ navigationConfig }) => {
  const { t, currentLanguage: language, changeLanguage: setLanguage, availableLanguages, languages } = useLanguage();
  const navigate = useNavigate();
  
  // Animation States (wie SlideDrawer)
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  
  // Menu States
  const [submenu, setSubmenu] = useState<DropdownType | null>(null);
  const [showLanguages, setShowLanguages] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(DRAWER_WIDTH_MOBILE);
  
  const drawerRef = useRef<HTMLDivElement>(null);

  // Responsive Drawer Width
  useEffect(() => {
    const updateDrawerWidth = () => {
      if (window.innerWidth >= TABLET_BREAKPOINT) {
        setDrawerWidth(DRAWER_WIDTH_TABLET);
      } else {
        setDrawerWidth(DRAWER_WIDTH_MOBILE);
      }
    };
    
    updateDrawerWidth();
    window.addEventListener('resize', updateDrawerWidth);
    return () => window.removeEventListener('resize', updateDrawerWidth);
  }, []);

  // Open Effect
  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      setShouldRender(true);
    }
  }, [isOpen]);

  // Body Scroll Lock - verhindert Scrollen im Hintergrund
  useEffect(() => {
    if (isOpen) {
      // Scroll sperren
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      // Scroll wieder freigeben
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    
    // Cleanup beim Unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [isOpen]);

  // Animation Trigger
  useEffect(() => {
    if (shouldRender && isOpen && !isClosing) {
      if (drawerRef.current) {
        drawerRef.current.offsetHeight; // Force reflow
      }
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    }
  }, [shouldRender, isOpen, isClosing]);

  // Close Effect
  useEffect(() => {
    if (!isOpen && shouldRender) {
      setIsClosing(true);
      setIsAnimating(false);
    }
  }, [isOpen, shouldRender]);

  // Transition End Handler
  const handleTransitionEnd = useCallback((e: React.TransitionEvent) => {
    if (e.propertyName === 'transform' && !isOpen) {
      setShouldRender(false);
      setIsClosing(false);
      setSubmenu(null);
      setShowLanguages(false);
    }
  }, [isOpen]);

  const openMenu = () => {
    setIsOpen(true);
    setSubmenu(null);
    setShowLanguages(false);
  };

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleLinkClick = (href: string) => {
    closeMenu();
    setTimeout(() => navigate(href), 100);
  };

  const getSubmenuTitle = (type: DropdownType): string => {
    const titles: Record<DropdownType, string> = {
      industries: t('navigation.industries'),
      services: t('navigation.services'),
      about: t('navigation.about'),
      career: t('navigation.career'),
    };
    return titles[type];
  };

  const getAllLinksForType = (type: DropdownType): LinkItem[] => {
    switch (type) {
      case 'industries':
        return [
          { name: t('industries.heading.viewAll'), href: '/industries', isHighlight: true },
          { name: t('industries.commercial-retail.title'), href: '/industries/commercial-retail' },
          { name: t('industries.food-reverage.title'), href: '/industries/food-reverage' },
          { name: t('industries.health-care.title'), href: '/industries/health-care' },
          { name: t('industries.offices-coworking.title'), href: '/industries/offices-coworking' },
          { name: t('industries.residential.title'), href: '/industries/residential' },
          { name: t('industries.logistics-industrial.title'), href: '/industries/logistics-industrial' },
          { name: t('industries.hospitality.title'), href: '/industries/hospitality' },
        ];
      case 'services':
        return [
          { name: t('services.heading.viewAll'), href: '/services', isHighlight: true },
          { name: t('services.design-build.title'), href: '/services/design-build' },
          { name: t('services.general-planning.title'), href: '/services/general-planning' },
          { name: t('services.construction-services.title'), href: '/services/construction-services' },
          { name: t('services.due-diligence.title'), href: '/services/due-diligence' },
          { name: t('services.energy-advisory.title'), href: '/services/energy-advisory' },
          { name: t('services.development-strategies.title'), href: '/services/development-strategies' },
          { name: t('services.fire-protection.title'), href: '/services/fire-protection' },
        ];
      case 'about':
        return [
          { name: t('dropdown.about.aboutItems.company'), href: '/about/company' },
          { name: t('dropdown.about.aboutItems.people'), href: '/about/people' },
          { name: t('dropdown.about.aboutItems.leadership'), href: '/about/leadership' },
          { name: t('dropdown.about.aboutItems.inclusion'), href: '/about/inclusion' },
          { name: t('dropdown.about.aboutItems.sustainability'), href: '/about/sustainability' },
          { name: t('dropdown.about.contactItems.location'), href: '/contact/location' },
          { name: t('dropdown.about.contactItems.contact'), href: '/contact' }
        ];
      case 'career':
        return [
          { name: t('dropdown.career.careerStart.findJobs'), href: '/career/jobs', isHighlight: true },
          { name: t('dropdown.career.careerStart.students'), href: '/career/students' },
          { name: t('dropdown.career.careerStart.university'), href: '/career/university' },
          { name: t('dropdown.career.careerStart.graduates'), href: '/career/graduates' },
          { name: t('dropdown.career.careerStart.experienced'), href: '/career/experienced' },
          { name: t('dropdown.career.expectations.benefits'), href: '/career/benefits' }
        ];
      default:
        return [];
    }
  };

  // Hamburger Styles
  const getHamburgerLineStyle = (position: 'top' | 'middle' | 'bottom'): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      width: `${HAMBURGER_LINE_WIDTH}px`,
      height: `${HAMBURGER_LINE_HEIGHT}px`,
      transformOrigin: 'center',
      transition: 'all 250ms ease-in-out'
    };

    if (position === 'top') {
      return {
        ...baseStyle,
        transform: isOpen 
          ? 'translateY(0px) rotate(45deg)' 
          : `translateY(-${HAMBURGER_LINE_SPACING}px) rotate(0deg)`
      };
    }
    if (position === 'middle') {
      return {
        ...baseStyle,
        opacity: isOpen ? 0 : 1,
        transform: 'translateY(0px) rotate(0deg)'
      };
    }
    return {
      ...baseStyle,
      transform: isOpen 
        ? 'translateY(0px) rotate(-45deg)' 
        : `translateY(${HAMBURGER_LINE_SPACING}px) rotate(0deg)`
    };
  };

  // Current Language Info
  const currentLang = languages[language];
  const duration = isClosing ? `${TRANSITION_CLOSE}ms` : `${TRANSITION_OPEN}ms`;

  return (
    <>
      {/* HEADER */}
      <header 
        className="bg-black text-white sticky top-0 z-50 relative"
        style={{ height: `${HEADER_HEIGHT}px` }}
      >
        <div className="max-w-[1700px] mx-auto h-full">
          <div className="flex justify-between items-center h-full px-4">

            {/* HAMBURGER BUTTON */}
            <div className="flex items-center justify-center h-full relative">
              <button
                onClick={isOpen ? closeMenu : openMenu}
                className="relative flex flex-col justify-center items-center text-gray-300 hover:text-white transition-colors focus:outline-none group"
                style={{ width: `${HAMBURGER_SIZE}px`, height: `${HAMBURGER_SIZE}px` }}
                aria-label={isOpen ? 'Menü schließen' : 'Menü öffnen'}
                aria-expanded={isOpen}
              >
                <span className="block bg-white absolute group-hover:bg-gray-300" style={getHamburgerLineStyle('top')} />
                <span className="block bg-white absolute group-hover:bg-gray-300" style={getHamburgerLineStyle('middle')} />
                <span className="block bg-white absolute group-hover:bg-gray-300" style={getHamburgerLineStyle('bottom')} />
              </button>
            </div>

            {/* LOGO */}
            <div className="flex items-center justify-center">
              <Link to="/" className="flex items-center" aria-label="Startseite" onClick={closeMenu}>
                <img 
                  src="/images/ESTATA-Logo-150.png" 
                  alt="ESTATA Logo" 
                  className="object-contain"
                  style={{ width: `${LOGO_WIDTH}px`, maxHeight: `${HEADER_HEIGHT}px` }}
                />
              </Link>
            </div>

            {/* LOGIN ICON */}
            <div className="flex items-center justify-center h-full">
              <Link 
                to="/login" 
                className="text-white hover:text-blue-400 transition-colors flex items-center justify-center p-2" 
                aria-label="Login"
                onClick={closeMenu}
              >
                <User style={{ width: `${ICON_SIZE}px`, height: `${ICON_SIZE}px` }} />
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* SLIDE DRAWER */}
      {shouldRender && (
        <div className="fixed inset-0 z-50" style={{ overflow: 'hidden' }}>
          {/* Backdrop */}
          <div
            onClick={closeMenu}
            className="absolute inset-0 bg-black transition-opacity ease-out"
            style={{
              opacity: isAnimating ? 0.5 : 0,
              transitionDuration: duration,
            }}
          />

          {/* Drawer Container - enthält Panel + Handle, fährt zusammen ein */}
          <div
            ref={drawerRef}
            className="absolute top-0 left-0 bottom-0 flex transition-transform ease-out"
            style={{
              width: `calc(${drawerWidth} + ${HANDLE_WIDTH}px)`,
              transform: isAnimating ? 'translateX(0)' : 'translateX(-100%)',
              transitionDuration: duration,
            }}
            onTransitionEnd={handleTransitionEnd}
          >
            {/* Drawer Panel */}
            <div
              className="bg-black shadow-2xl border-r border-gray-800 flex flex-col"
              style={{
                width: drawerWidth,
                height: '100%',
                maxHeight: '100dvh',
              }}
            >
              {/* Header - gleiche Höhe wie Website-Header */}
              <div 
                className="flex items-center justify-between px-5 border-b border-gray-800 flex-shrink-0"
                style={{ height: `${HEADER_HEIGHT}px` }}
              >
                {/* 1. Ebene: "Menü" | 2. Ebene: "Zurück" */}
                {!submenu && !showLanguages ? (
                  <span className="text-white font-medium text-[15px]">{t('navigation.menu')}</span>
                ) : (
                  <button
                    onClick={() => {
                      if (submenu) setSubmenu(null);
                      if (showLanguages) setShowLanguages(false);
                    }}
                    className="flex items-center text-[#37acdc] hover:text-[#37acdc]/80 transition-colors -ml-1"
                  >
                    <ChevronLeft size={20} className="mr-1" />
                    <span className="text-[15px] font-medium">{t('navigation.back')}</span>
                  </button>
                )}
                
                <button 
                  onClick={closeMenu}
                  className="p-2 rounded-full hover:bg-gray-800 transition-colors -mr-2"
                  aria-label={t('navigation.close')}
                >
                  <X size={20} className="text-gray-400" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto overscroll-contain">
                
                {/* Main Menu */}
                {!submenu && !showLanguages && (
                  <div>
                    <nav>
                      {navigationConfig.map((item) => (
                        <div key={item.name}>
                          {item.hasDropdown ? (
                            <button
                              onClick={() => setSubmenu(item.dropdownType!)}
                              className="mobile-menu-item flex items-center justify-between w-full px-5 border-b border-gray-800/50"
                              style={{ height: `${MENU_ITEM_HEIGHT}px` }}
                            >
                              <span className="text-[15px] font-medium">{item.name}</span>
                              <ChevronRight size={18} className="text-gray-500" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleLinkClick(item.href)}
                              className="mobile-menu-item flex items-center w-full px-5 text-left border-b border-gray-800/50"
                              style={{ height: `${MENU_ITEM_HEIGHT}px` }}
                            >
                              <span className="text-[15px] font-medium">{item.name}</span>
                            </button>
                          )}
                        </div>
                      ))}
                    </nav>

                    {/* Divider */}
                    <div className="h-px bg-gray-700" />

                    {/* Language & Login */}
                    <button
                      onClick={() => setShowLanguages(true)}
                      className="mobile-menu-item flex items-center justify-between w-full px-5"
                      style={{ height: `${MENU_ITEM_HEIGHT}px` }}
                    >
                      <div className="flex items-center gap-3">
                        <Globe size={18} className="text-gray-400" />
                        <span className="text-[15px]">{currentLang?.name || 'Deutsch'}</span>
                      </div>
                      <ChevronRight size={18} className="text-gray-500" />
                    </button>

                    <button
                      onClick={() => handleLinkClick('/login')}
                      className="mobile-menu-item flex items-center w-full px-5"
                      style={{ height: `${MENU_ITEM_HEIGHT}px` }}
                    >
                      <User size={18} className="text-gray-400 mr-3" />
                      <span className="text-[15px]">MyEST Login</span>
                    </button>
                  </div>
                )}

                {/* Submenu View - ohne Back-Button (ist jetzt im Header) */}
                {submenu && (
                  <div className="animate-slide-in">
                    <nav>
                      {/* Submenu Links */}
                      {getAllLinksForType(submenu).map((item) => (
                        <button
                          key={item.name}
                          onClick={() => handleLinkClick(item.href)}
                          className="mobile-menu-item flex items-center w-full px-5 text-left border-b border-gray-800/50"
                          style={{ height: `${MENU_ITEM_HEIGHT}px` }}
                        >
                          <span className="text-[14px]">{item.name}</span>
                        </button>
                      ))}
                    </nav>
                  </div>
                )}

                {/* Language Selection */}
                {showLanguages && (
                  <div className="animate-slide-in">
                    {/* Aktuelle Sprache - hervorgehoben */}
                    <button
                      className="flex items-center justify-between w-full px-5 text-[#37acdc] bg-gray-900/50 border-b border-gray-800/50"
                      style={{ height: `${MENU_ITEM_HEIGHT}px` }}
                      disabled
                    >
                      <div className="flex items-center gap-3">
                        <Globe size={18} />
                        <div className="leading-tight text-left">
                          <div className="font-medium text-[15px]">{currentLang?.name}</div>
                          <div className="text-gray-500 text-[11px]">{t('language.currentLanguage')}</div>
                        </div>
                      </div>
                      <Check size={16} />
                    </button>

                    {/* Andere Sprachen */}
                    {availableLanguages
                      .filter(langCode => langCode !== language)
                      .map((langCode) => {
                        const lang = languages[langCode];
                        return (
                          <button
                            key={langCode}
                            onClick={() => {
                              setLanguage(langCode);
                              setShowLanguages(false);
                            }}
                            className="mobile-menu-item flex items-center w-full px-5 text-left border-b border-gray-800/50"
                            style={{ height: `${MENU_ITEM_HEIGHT}px` }}
                          >
                            <Globe size={18} className="text-gray-400 mr-3" />
                            <div className="leading-tight">
                              <div className="font-medium text-[15px]">{lang?.name}</div>
                              <div className="text-gray-500 text-[11px]">{lang?.name_en || lang?.name}</div>
                            </div>
                          </button>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>

            {/* Close Handle - Teil des Containers, fährt synchron mit */}
            <button
              onClick={closeMenu}
              className="flex-shrink-0 flex items-center justify-center bg-gray-800 hover:bg-gray-700 active:bg-gray-600 rounded-r-xl transition-colors self-center"
              style={{ width: `${HANDLE_WIDTH}px`, height: 72 }}
              aria-label="Menü schließen"
            >
              <ChevronLeft size={18} className="text-gray-400" />
            </button>
          </div>
        </div>
      )}

      {/* Animation Styles */}
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slide-in {
          animation: slideIn 200ms ease-out;
        }
      `}</style>
    </>
  );
};

export default HeaderMobile;
